import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Calculator, ShieldCheck, Lightning, Certificate } from "@phosphor-icons/react";
import BipartiteGraph from "../viz/BipartiteGraph";

const SKOLKOVO_LOGO =
  "/external-assets/5jvrcdjd_Sk_Sk-block-green-ru_word.png";

const Badge = ({ children, testid }) => (
  <div
    data-testid={testid}
    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mono text-[10px] uppercase tracking-[0.18em] text-brand-cyan"
  >
    <span className="w-1.5 h-1.5 rounded-full bg-brand-cyan shadow-[0_0_10px_#0099C9]" />
    {children}
  </div>
);

const SkolkovoBadge = ({ testid }) => (
  <div
    data-testid={testid}
    className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full glass"
  >
    <img src={SKOLKOVO_LOGO} alt="Сколково" className="h-4 w-auto object-contain" loading="lazy" />
    <span className="mono text-[10px] uppercase tracking-[0.18em] text-brand-cyan">Резидент</span>
  </div>
);

export const Hero = ({ onDemo, onROI }) => {
  return (
    <section id="top" data-testid="hero-section" className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden">
      {/* Background grid + gradients */}
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="absolute inset-x-0 top-0 h-[600px] pointer-events-none">
        <div className="absolute top-20 -left-32 w-[480px] h-[480px] rounded-full bg-brand/20 blur-[120px]" />
        <div className="absolute top-40 right-0 w-[420px] h-[420px] rounded-full bg-brand-cyan/15 blur-[120px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
        {/* LEFT: copy */}
        <div className="lg:col-span-7 z-10">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap gap-2 mb-6"
          >
            {/* TEMP HIDDEN: <Badge testid="badge-nvidia">NVIDIA Inception Member</Badge> */}
            <SkolkovoBadge testid="badge-skolkovo" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-heading font-black tracking-tighter leading-[1.02] text-4xl sm:text-5xl lg:text-6xl"
            data-testid="hero-headline"
          >
            Катализатор для вашей
            <br />
            <span className="text-brand-cyan text-glow-cyan">ИИ-инфраструктуры:</span>
            <br />
            утилизация GPU{" "}
            <span className="mono text-brand bg-gradient-to-r from-brand to-brand-cyan bg-clip-text text-transparent">
              до 85%
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-7 max-w-2xl text-muted text-base sm:text-lg leading-relaxed"
            data-testid="hero-subheadline"
          >
            Перестаньте сжигать 60% бюджета на простаивающем «железе». InBoost использует
            обучение с подкреплением{" "}
            <span className="mono text-ink-text">(RL)</span> и графовые нейросети{" "}
            <span className="mono text-ink-text">(GNN)</span>, чтобы математически точно
            стыковать задачи с ресурсами в реальном времени.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button
              onClick={onDemo}
              data-testid="hero-cta-demo"
              className="btn-primary rounded-lg px-6 py-3.5 font-semibold inline-flex items-center gap-2"
            >
              Запросить демо <ArrowRight size={18} weight="bold" />
            </button>
            <button
              onClick={onROI}
              data-testid="hero-cta-roi"
              className="btn-secondary rounded-lg px-6 py-3.5 font-semibold inline-flex items-center gap-2"
            >
              <Calculator size={18} weight="bold" /> Рассчитать ROI
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs mono uppercase tracking-widest text-muted"
            data-testid="hero-trust-strip"
          >
            <div className="flex items-center gap-2">
              <Lightning size={14} weight="fill" className="text-brand-cyan" />
              Повышение ёмкости дата-центра
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck size={14} weight="fill" className="text-brand-cyan" />
              Production-grade на Kubernetes
            </div>
            <div className="flex items-center gap-2" data-testid="hero-patent">
              <Certificate size={14} weight="fill" className="text-brand-cyan" />
              Запатентованная технология
            </div>
          </motion.div>
        </div>

        {/* RIGHT: visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative rounded-2xl glass-strong p-5 overflow-hidden animate-pulse-glow">
            <div className="flex items-center justify-between mb-3">
              <div className="mono text-[10px] uppercase tracking-[0.18em] text-brand-cyan">
                Bipartite Matching · RT
              </div>
              <div className="flex gap-1.5">
                <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
                <span className="mono text-[10px] text-muted">LIVE</span>
              </div>
            </div>
            <BipartiteGraph className="w-full h-[360px]" />
            <div className="mt-2 mono text-[10px] text-muted">
              Σ Score = КЭИ × (1 − ЗКЗ) → policy: <span className="text-brand-cyan">argmax</span>
            </div>
            <div className="scanline" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
